<template>
  <div class="reviews-card">
      <div class="review-filter-v2">
        <div class="reviews-sts">
          <div class="sts-total">
            <p class="sts-score">
              <span class="current-rate">{{ reviewsSts.score || 5 }}</span><span class="max-rate">&nbsp;/&nbsp;5</span>
            </p>
            <van-rate class="rate-icons" :model-value="Number(reviewsSts.score || '5')" allow-half size="25" void-icon="star" void-color="#ebebeb" />
            <p class="ratings">({{ reviewsSts.total || 0 }} Ratings)</p>
          </div>

          <div class="sts-star">
            <div
              v-for="(item, idx) in reviewsSts.scoreList"
              :key="idx"
              class="score-rate-item"
            >
              <p class="label rate-label">
                <van-rate :model-value="item.score" allow-half size="22"  void-icon="star" void-color="#ebebeb"/>
              </p>
              <div class="process-bar">
                <div
                  class="inner-process-bar"
                  :style="{ width: reviewsSts.total <= 0 ? 0 : `${item.total / (reviewsSts.total || 0) * 100}%` }"
                ></div>
              </div>
              <p class="total">{{ item.total }}</p>
            </div>
          </div>
        </div>
        <div class="filter-bar">
          <div
            v-for="(item, idx) in reviewsSts.keywords"
            :key="idx"
            class="filter-tab-item"
            :select="item.isSelect ? '1' : '0'"
            @click="handleReviewKeywordClick(idx, item.queryType, 'keywords')"
          >          
            {{ item.label }} <span v-if="item.total > 0">({{ item.total }})</span>
          </div>
          <div class="filter-tab-item rate-filter-tab" :select="reviewsSts.scoreList.some(c => c.isSelect) ? '1' : '0'">
            {{ startFilterLabel }} <span class="jt"><van-icon name="play" size="14" /></span>

            <div class="rate-filters">
              <div
                v-for="(item, idx) in reviewsSts.scoreList"
                :key="idx"
                class="rate-filter-item"
                @click="handleReviewKeywordClick(idx, item.queryType, 'scoreList')"
              >
                {{ `${item.score} Stars` }} <span>({{ item.total }})</span>
              </div>
            </div>
          </div>
          <div class="hide-system-reviews">
            <van-checkbox v-model="isSourceType" icon-size="14px">Hide system default reviews.</van-checkbox>
          </div>
        </div>
      </div>

      <div class="reviews-list">
        <p v-show="loading" class="loading-p">
          <van-loading type="spinner" color="#DD3131" />
        </p>

        <KReviewsItem
          v-for="review in reviewsList"
          :key="review.commentContent?.id"
          :review-item="review"
          :isPC="true"
          @update:likes="handleUpdateReviewLikes(review)"
        />
        
        <div class="no-data" v-if="reviewsList.length <= 0">Not Reviews</div>
      </div>

      <div class="pagination-box">
        <van-pagination :model-value="payload.pageNo" :page-count="maxPage" :items-per-page="payload.pageSize" @change="handlePageClick" />
      </div>
    </div>
</template>

<script setup lang="ts">
import {
  getCommentList,
  type IReviewPayload,
  type IReviewsListTab
} from '@/api/models/reviews'
import { LazyQueue } from '../mixins';

const queue = new LazyQueue()
const props = defineProps<{ listingId: number, commentStat?: any }>()
const { $tz } = useNuxtApp()
const { $getCountry } = useNuxtApp()

const payload = reactive<IReviewPayload>({
  pageNo: 1,
  pageSize: 10,
  queryType: 'normal'
})
const isSourceType = ref(false)

const maxPage = ref<number>(1)
const handlePageClick = async(e: number) => {
  payload.pageNo = e

  await initReviewsList()
}
const reviewsSts = reactive<{
  score: number,
  total: number,
  keywords: { label: string, total: number, queryType: number | string, isSelect: boolean }[],
  scoreList: { score: number, total: number, queryType: number, isSelect: boolean }[]
}>({
  score: 0,
  total: 0,
  keywords: [],
  scoreList: []
})
const startFilterLabel = computed<string>(() => {
  const cur = reviewsSts.scoreList.find(c => c.isSelect)

  return cur ? `${cur.score} Stars (${cur.total})` : 'All Stars'
})
const initRatingInfo = async(commentRes: any) => {

  if(!commentRes) {
    return
  }

  const {
    stars,
    oneStarNum,
    twoStarNum,
    threeStarNum,
    fourStarNum,
    fiveStarNum,
    totalNum,
    withImageOrVideoNum,
    withTextNum,
    withVideoNum
  } = commentRes.listingCommentStat


  const commentTask = () => {
    reviewsSts.scoreList = [
      { score: 5, total: Number(fiveStarNum), queryType: 5, isSelect:false },
      { score: 4, total: Number(fourStarNum), queryType: 4, isSelect:false },
      { score: 3, total: Number(threeStarNum), queryType: 3, isSelect:false },
      { score: 2, total: Number(twoStarNum), queryType: 2, isSelect:false },
      { score: 1, total: Number(oneStarNum), queryType: 1, isSelect:false },
    ]
    reviewsSts.score = commentRes.listingScore
    reviewsSts.total = commentRes.commentTotalNum
    reviewsSts.keywords = [
      { label: 'All Reviews', total: reviewsSts.total, queryType: 'normal', isSelect: true },
      { label: 'Latest', total: 0, queryType: 'latest', isSelect: false },
      { label: 'With Content', total: Number(withTextNum), queryType: 'has_text', isSelect: false },
      { label: 'With Photos', total: Number(withImageOrVideoNum), queryType: 'has_image_or_video', isSelect: false },
    ]
  }

  queue.addTask(commentTask)
}
watch(() => props.commentStat, initRatingInfo)

// const emits = defineEmits<{ (e: 'reviews:sts', v: any): void }>()

const isOver = ref<boolean>(false)
const loading = ref<boolean>(false)
const reviewsList = ref<any[]>([])
const initReviewsList = async(isRefresh: boolean = false) => {
  if((isOver.value || loading.value) && !isRefresh) {
    return
  }

  if(isRefresh) {
    payload.pageNo = 1
  }
  const params:IReviewsListTab = {
    listing_id: Number(props.listingId),
    offset: (payload.pageNo - 1) * payload.pageSize,
    limit: payload.pageSize,
    order_by: 'normal'
  }

  if (typeof payload.queryType === 'number') {
    params.product_score = payload.queryType
  } else {
    switch (payload.queryType) {
      case 'latest':
      params.order_by = payload.queryType
        break; 
      case 'has_text':
      params.has_text = true
        break; 
      case 'has_image_or_video':
      params.has_image_or_video = true
        break; 
      default:
        break;
    }
  }
  loading.value = true
  if(isSourceType.value) {
    params.source_type = 1
  } else {
    // 删除source_type
    delete params.source_type
  }
  const res = await getCommentList(params)
  
  const commentListTask = () => {
    maxPage.value = Math.ceil(res.total / payload.pageSize)
    isOver.value = res?.total < payload.pageSize

    const _list = (res.items || []).map((c: any) => {
      const timeStr = $tz.zeroToLocalZonetime(new Date(c.commentBase?.createdTime), { format: 'yyyy-MM-dd' })
      const timeAry = timeStr.split('-')
      const m = $tz.monthToEn(Number(timeAry[1]))
      timeAry.splice(1, 1, m)

      c.replyTime = `${m} ${timeAry[2]}.${timeAry[0]}`
      return c
    })

    reviewsList.value = _list
  }

  queue.addTask(commentListTask)
  
  loading.value = false
  refreshing.value = false
}
watch(isSourceType, () => {
  initReviewsList(true)
})
const refreshing = ref<boolean>(false)

const handleReviewKeywordClick = async(idx: number, queryType: number | string, type: 'keywords' | 'scoreList') => {
  [...reviewsSts.keywords, ...reviewsSts.scoreList].forEach((c: any) => {
    c.isSelect = false
  })
  reviewsSts[type][idx].isSelect = true

  payload.queryType = queryType
  await initReviewsList(true)
}

const handleUpdateReviewLikes = (reviewItem: any) => {
  reviewItem.commentBase.likesCount += reviewItem.commentContent.isLiked ? -1 : 1
  reviewItem.commentContent.isLiked = !reviewItem.commentContent.isLiked
}

const reviewsInit = () => { props.listingId > 0 && initReviewsList() }
onMounted(reviewsInit)
</script>

<style lang="scss" scoped>
.reviews-card :deep(.content-text){
  font-size: 32px;
  margin: 10px 0;
}
.reviews-card :deep(.icon){
  width: 100px;
  height: 100px;
}
.reviews-card :deep(.review-content){
  padding-left: 100px;
}
.reviews-card :deep(.reply-text){
  font-size: 28px;
  margin: 10px 0 0 40px
}
.reviews-card :deep(.user-info){
  .user-name{
    font-size: 32px;
  }
  .review-time{
    font-size: 26px;
  }
}

.reviews-card{
  background-color: #fff;
  padding: 30px;
  .review-filter-v2{
    margin-bottom: 40px;
    .reviews-sts::v-deep(.van-rate) {
      cursor: auto;
    }
    .reviews-sts{
      display: flex;
      margin: 60px 0;
      .sts-total{
        border-right: 1px solid #DDDFE4;
        padding: 0 80px;
        margin-right: 80px;
        .sts-score{
          margin-bottom: 15px;
          .current-rate{ color: var(--pc-default-color); font-size: 124px; text-shadow: 0 0 2px var(--pc-default-color); }
          .max-rate{ font-size: 72px; color: #666666; }
        }
        
        .ratings{ margin-top: 20px;color: #999999; font-size: 38px; }
      }
      .sts-star{
        flex: 1;
        padding-right: 40px;
        .score-rate-item{
          display: flex;
          align-items: center;
          .rate-label{width: 260px;}
          .total{min-width: 140px;color:#999999}
          .process-bar{
            margin: 20px 40px;
            position: relative;
            flex: 1;
            height: 30px;
            overflow: hidden;
            border-radius: 30px;
            background-color: #EFF0F5;
            .inner-process-bar{
              position: absolute;
              left: 0;
              top: 0;
              bottom: 0;
              width: 80%;
              border-radius: 30px;
              background-color: var(--pc-default-color);
            }
          }
        }
      }
    }
    .filter-bar{
      display: flex;
      align-items: center;
      border-bottom: 1px solid #efefef;
      .rate-filter-tab:hover > .rate-filters{ display: block;}
      .hide-system-reviews {
        background: white;
        text-align: right;
        padding-bottom: 10px;
        width: 100%;
        display: flex;
        justify-content: flex-end;
        padding-right: 20px;
      }
      .filter-tab-item{
        white-space: nowrap;
        font-size: 36px;
        color: #666666;
        cursor: pointer;
        position: relative;
        height: 100px;
        padding: 0 60px;
        line-height: 100px;
        .jt i{
          transform: rotateZ(90deg);
        }
        .rate-filters{
          position: absolute;
          left: 0;
          top: 100%;
          width: 360px;
          display: none;
          text-align: center;
          background-color: #fff;
          border: 1px solid #efefef;
          padding: 0 30px;
          z-index: 99;
          .rate-filter-item{
            color: #666;
            font-weight: 400;
            border-bottom: 1px solid #efefef;
            span{color: #999;}
          }
        }
      }
      .filter-tab-item[select="1"]{color: var(--pc-default-color); font-weight: 600;}
      .filter-tab-item[select="1"]::after{
        content: " ";
        width: 30%;
        height: 8px;
        background-color: var(--pc-default-color);
        border-radius: 4px;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        margin: 0 auto;
      }
    }
  }
}
.reviews-list{
  margin-top: 20px;
  padding: 0 30px;
  background-color: #fff;
  .loading-p{
    text-align: center;
  }
  :deep(.reviews-item){
    border-bottom: 1px solid #efefef;
    margin: 0;
    &:last-child{
      border: none
    }
  }
}
.no-data{
  text-align: center;
  padding: 80px;
  color: #666;
}
.pagination-box{
  margin-top: 60px;
}
</style>
